import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageCircle, MessageSquare, Bell, CheckCheck, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export default function NotificationsPanel({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { notifications, totalUnread, markRead, markAllRead, fetchNotifications } = useNotifications();

  const handleNotificationClick = async (notif) => {
    if (!notif.read) await markRead(notif.id);
    onClose();
    if (notif.type === 'message') {
      sessionStorage.setItem('sg_message_recipient', JSON.stringify({
        id: notif.reference_id,
        name: notif.from_user_name,
      }));
      navigate('/messages');
    } else if (notif.type === 'comment') {
      navigate('/home');
    }
  };

  const handleMarkAll = async () => {
    await markAllRead();
    fetchNotifications();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Panel — Fullscreen */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed inset-0 z-50 bg-background overflow-hidden"
            data-testid="notifications-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold">{t('notifications') || 'Notifications'}</h2>
                {totalUnread > 0 && (
                  <span className="text-[10px] bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5 font-bold">
                    {totalUnread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {totalUnread > 0 && (
                  <button
                    onClick={handleMarkAll}
                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 font-medium"
                    data-testid="notifications-mark-all-read"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    {t('mark_all_read') || 'Mark all read'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full hover:bg-accent transition-colors"
                  data-testid="notifications-close"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto" style={{ height: 'calc(100vh - 52px)' }}>
              {notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t('no_notifications') || 'No notifications yet'}</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50 border-b border-border/50 ${
                        !notif.read ? 'bg-primary/5' : ''
                      }`}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      {/* Icon */}
                      <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        notif.type === 'message' ? 'bg-blue-500/10' : 'bg-amber-500/10'
                      }`}>
                        {notif.from_user_photo ? (
                          <img src={notif.from_user_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : notif.type === 'message' ? (
                          <MessageCircle className="w-4 h-4 text-blue-500" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-relaxed ${!notif.read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                          {notif.body}
                        </p>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">{timeAgo(notif.created_at)}</p>
                      </div>
                      {/* Unread dot */}
                      {!notif.read && (
                        <div className="mt-2 w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
